// Shared types for the admin Competitions pages, mirroring the API contract in
// apps/api/src/routes/competitions-admin.ts + lib/competition-standings.ts.

export type CompetitionFormat = "LEAGUE" | "GROUPS_KNOCKOUT" | "KNOCKOUT";
export type CompetitionStatus =
  | "DRAFT"
  | "REGISTRATION_OPEN"
  | "REGISTRATION_CLOSED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED";
export type ParticipantStatus =
  | "PENDING"
  | "CONFIRMED"
  | "WITHDRAWN"
  | "REJECTED";

export interface CompetitionListItem {
  id: string;
  name: string;
  season: string | null;
  format: CompetitionFormat;
  status: CompetitionStatus;
  sportId: string;
  sport: string;
  participantCount: number;
  createdAt: string;
}

export interface CompetitionListResponse {
  competitionsEnabled: boolean;
  subscriptionTier: "BASIC" | "PRO" | "ENTERPRISE";
  sports: { id: string; name: string }[];
  competitions: CompetitionListItem[];
}

export interface MatchSet {
  a: number;
  b: number;
  tb?: number;
}

export interface FixtureSide {
  participantId: string;
  name: string;
  handle: string | null;
  seed: number | null;
}

export interface Fixture {
  id: string;
  phase: "GROUP" | "KNOCKOUT" | null;
  round: number | null;
  bracketSlot: number | null;
  isThirdPlace: boolean;
  divisionLabel: string | null;
  groupName: string | null;
  status: string;
  confirmationStatus: string;
  winnerSide: number | null;
  sets: MatchSet[];
  scoreSummary: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  home: FixtureSide | null;
  away: FixtureSide | null;
  viewerSide: number | null;
  viewerCanReport: boolean;
  viewerCanConfirm: boolean;
}

export interface StandingsRow {
  participantId: string;
  name: string;
  seed: number | null;
  position: number;
  played: number;
  won: number;
  lost: number;
  drawn: number;
  setsFor: number;
  setsAgainst: number;
  gamesFor: number;
  gamesAgainst: number;
  points: number;
}

export interface BracketRound {
  round: number;
  roundCode: string;
  matches: Fixture[];
}

export interface Bracket {
  rounds: BracketRound[];
  thirdPlace: Fixture | null;
}

export interface DivisionDetail {
  label: string | null;
  groups: { name: string | null; standings: StandingsRow[] }[];
  bracket: Bracket | null;
  fixtures: Fixture[];
}

export interface SerializedCompetition {
  id: string;
  name: string;
  season: string | null;
  sport: string;
  format: CompetitionFormat;
  status: CompetitionStatus;
  description: string | null;
  registrationOpensDate: string | null;
  registrationClosesDate: string | null;
  startDate: string | null;
  endDate: string | null;
  thirdPlaceMatch: boolean;
  divisions: string[];
  scoring: { pointsWin: number; pointsDraw: number; pointsLoss: number };
  bestOfSets: number | null;
  maxParticipants: number | null;
  participantCount: number;
  divisionsDetail: DivisionDetail[];
  myParticipant: unknown;
}

export interface AdminParticipant {
  id: string;
  name: string;
  handle: string | null;
  playerUserId: string | null;
  partnerName: string | null;
  phone: string | null;
  email: string | null;
  status: ParticipantStatus;
  divisionLabel: string | null;
  groupName: string | null;
  seed: number | null;
  note: string | null;
  registeredAt: string;
}

export interface CompetitionDetailResponse {
  competition: SerializedCompetition;
  sportId: string;
  participants: AdminParticipant[];
}

export const COMPETITION_FORMATS: CompetitionFormat[] = [
  "LEAGUE",
  "GROUPS_KNOCKOUT",
  "KNOCKOUT",
];

export const COMPETITION_STATUSES: CompetitionStatus[] = [
  "DRAFT",
  "REGISTRATION_OPEN",
  "REGISTRATION_CLOSED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
];
